import { useState, useEffect, useCallback } from 'react';
import {
  Play, Square, RefreshCw, ExternalLink, Loader2, Globe, AlertTriangle, Box, Power,
} from 'lucide-react';
import { useStore } from '../../store/useStore';

interface PreviewAppProps {
  projectId?: string;
  title?: string;
}

export function PreviewApp({ projectId, title }: PreviewAppProps) {
  const currentProject = useStore((s) => s.currentProject);
  const addNotification = useStore((s) => s.addNotification);
  const openApp = useStore((s) => s.openApp);
  const apiBase = import.meta.env.VITE_API_URL || '';

const activeProjectId = projectId || currentProject?.id;
const activeProjectName: string = title || currentProject?.name || 'Project';

  const [running, setRunning] = useState(false);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [frameKey, setFrameKey] = useState(0);

  const proxyUrl = activeProjectId ? `${apiBase}/proxy/${activeProjectId}/` : null;

  const checkStatus = useCallback(async () => {
    if (!activeProjectId) return;
    try {
      const res = await fetch(`${apiBase}/api/preview/status?projectId=${activeProjectId}`, { credentials: 'include' });
      const data = await res.json();
      setRunning(!!data.running);
      setError(null);
    } catch {
      setRunning(false);
    }
  }, [activeProjectId, apiBase]);

  useEffect(() => {
    checkStatus();
    const timer = setInterval(checkStatus, 8000);
    return () => clearInterval(timer);
  }, [checkStatus]);

  const startPreview = async () => {
    if (!activeProjectId) {
      addNotification('Info', 'Open a project first', 'info');
      openApp('projects');
      return;
    }
    setStarting(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/preview/start`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: activeProjectId }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to start preview');
      }
      setRunning(true);
      setFrameKey((k) => k + 1);
      addNotification('Success', 'Preview server started', 'success');
    } catch (err: any) {
      setError(err.message);
      setRunning(false);
      addNotification('Error', err.message, 'error');
    } finally {
      setStarting(false);
    }
  };

  const stopPreview = async () => {
    if (!activeProjectId) return;
    setStopping(true);
    try {
      await fetch(`${apiBase}/api/preview/stop`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: activeProjectId }),
      });
      setRunning(false);
      addNotification('Info', 'Preview server stopped', 'info');
    } catch {
      addNotification('Error', 'Failed to stop server', 'error');
    } finally {
      setStopping(false);
    }
  };

  if (!activeProjectId) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-4 p-8 bg-desktop-surface2 text-center">
        <div className="w-14 h-14 rounded-2xl bg-desktop-surface flex items-center justify-center">
          <Globe className="w-7 h-7 text-desktop-accent" />
        </div>
        <div>
          <h2 className="text-base font-medium text-desktop-text">No project open</h2>
          <p className="text-sm text-desktop-text/60 mt-1 max-w-xs">
            Open a project to preview its development server.
          </p>
        </div>
        <button onClick={() => openApp('projects')} className="px-4 py-2 text-sm bg-desktop-accent text-white rounded-lg hover:opacity-90">
          Open Projects
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-desktop-surface2">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-1.5 bg-desktop-surface border-b border-desktop-border shrink-0 flex-wrap">
        <div className="flex items-center gap-1.5 min-w-0">
          <Power className={`w-3.5 h-3.5 ${running ? 'text-desktop-success' : 'text-desktop-text/40'}`} />
          <span className="text-xs font-medium text-desktop-text truncate">Preview · {activeProjectName}</span>
        </div>
        <div className="flex-1" />
        {running && (
          <span className="text-[10px] text-desktop-success flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-desktop-success animate-pulse" />
            Running
          </span>
        )}
        <button
          onClick={checkStatus}
          className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-desktop-hover text-desktop-text/60 hover:text-desktop-text transition-colors"
          title="Check status"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
        {!running ? (
          <button
            onClick={startPreview}
            disabled={starting}
            className="flex items-center gap-1.5 px-3 h-8 text-xs bg-desktop-success text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 font-medium"
          >
            {starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
            {starting ? 'Starting...' : 'Start Dev Server'}
          </button>
        ) : (
          <>
            <button
              onClick={() => setFrameKey((k) => k + 1)}
              className="flex items-center gap-1.5 px-3 h-8 text-xs bg-desktop-surface2 border border-desktop-border text-desktop-text rounded-lg hover:bg-desktop-hover transition-colors"
              title="Restart (reload frame)"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Reload
            </button>
            <button
              onClick={stopPreview}
              disabled={stopping}
              className="flex items-center gap-1.5 px-3 h-8 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 transition-opacity disabled:opacity-50"
            >
              {stopping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Square className="w-3.5 h-3.5" />}
              Stop
            </button>
            <a
              href={proxyUrl ?? undefined}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 px-3 h-8 text-xs bg-desktop-accent text-white rounded-lg hover:opacity-90 transition-opacity"
              title="Open in new tab"
            >
              <ExternalLink className="w-3.5 h-3.5" /> New Tab
            </a>
          </>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 bg-white relative">
        {error && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="glass rounded-2xl max-w-md p-6 text-center">
              <AlertTriangle className="w-10 h-10 text-desktop-warning mx-auto mb-3" />
              <p className="text-sm font-medium text-desktop-text mb-1">Could not start preview</p>
              <p className="text-xs text-desktop-text/60 break-words mb-4">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  openApp('terminal', { projectName: activeProjectName, projectId: activeProjectId });
                }}
                className="text-xs text-desktop-accent hover:underline"
              >
                Open terminal to investigate
              </button>
            </div>
          </div>
        )}

        {!running ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-center px-6">
            <div className="w-14 h-14 rounded-2xl bg-desktop-surface flex items-center justify-center">
              <Box className="w-7 h-7 text-desktop-text/50" />
            </div>
            <p className="text-sm text-desktop-text/60 max-w-sm">
              Start the development server to preview your app here.
            </p>
            <button
              onClick={startPreview}
              disabled={starting}
              className="text-xs text-desktop-accent hover:underline disabled:opacity-50"
            >
              {starting ? 'Starting...' : 'Click "Start Dev Server" above, or run `npm run dev` in the terminal'}
            </button>
          </div>
        ) : (
          proxyUrl && (
            <iframe
              key={frameKey}
              src={proxyUrl}
              className="w-full h-full border-0"
              allow="camera; microphone; clipboard-read; clipboard-write"
            />
          )
        )}
      </div>

      {/* Status bar */}
      <div className="flex items-center justify-between px-3 py-1 border-t border-desktop-border bg-desktop-surface shrink-0">
        <span className="text-[10px] text-desktop-text/50">
          {running ? 'Preview proxy active' : 'Preview stopped'}
        </span>
        <a
          href={`${apiBase}/proxy/${activeProjectId}/`}
          target="_blank"
          rel="noreferrer"
          className="text-[10px] text-desktop-accent hover:underline"
        >
          {proxyUrl}
        </a>
      </div>
    </div>
  );
}